import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LucideIcon } from 'lucide-react-native';
import { ReactNode, memo } from 'react';
import { View } from 'react-native';
import { BiSurfaceCard } from '../../components/ui/BiSurfaceCard';

/** The frame every library tile shares: icon, name and tags, lines. */
export const LibraryTile = memo(
  ({
    icon: Icon,
    color,
    name,
    tags,
    lines,
    tone,
    action,
    accessibilityLabel,
    onPress,
  }: {
    icon: LucideIcon;
    color: string;
    name: string;
    tags?: ReactNode;
    /** Short facts, one per line; empty ones are left out. */
    lines: Array<string | null>;
    /** The last line reads as a problem. */
    tone?: 'danger';
    /** A button at the tile's end. */
    action?: ReactNode;
    accessibilityLabel?: string;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const shown = lines.filter((line): line is string => !!line);
    return (
      <BiSurfaceCard
        onPress={onPress}
        accessibilityLabel={accessibilityLabel}
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: rpx(12),
          padding: rpx(14),
        }}
      >
        <View
          style={{
            width: rpx(40),
            height: rpx(40),
            borderRadius: rpx(10),
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: tint(color, 0.14),
          }}
        >
          <Icon size={20} color={color} />
        </View>
        <View style={{ flex: 1, gap: rpx(4) }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: rpx(6),
            }}
          >
            <Typography.Text
              medium
              size={rpx(14)}
              color={aiColors.foreground}
              numberOfLines={1}
              style={{ flexShrink: 1 }}
            >
              {name}
            </Typography.Text>
            {tags}
          </View>
          {shown.map((line, index) => (
            <Typography.Text
              key={index}
              size={rpx(12)}
              color={
                tone === 'danger' && index === shown.length - 1
                  ? aiColors.destructive
                  : aiColors.mutedForeground
              }
              numberOfLines={2}
            >
              {line}
            </Typography.Text>
          ))}
        </View>
        {action}
      </BiSurfaceCard>
    );
  },
);
